import { delaySignal } from '@tempots/beatui'
import {
  attr,
  computedOf,
  html,
  prop,
  Signal,
  type TNode,
  WithBrowserCtx,
} from '@tempots/dom'
import { WindowSize } from '@tempots/ui'

/**
 * Scales a fixed-size page (816×1056 etc.) down to fit the available pane
 * width, so the preview never scrolls horizontally. Content renders at the
 * page's natural pixel size inside a transform-scaled wrapper.
 */
export function ScaledPage(
  size: Signal<{ w: number; h: number }>,
  ...content: TNode[]
) {
  return WithBrowserCtx(ctx =>
    WindowSize(rect => {
      // Nudge one frame after mount so the first measurement happens after
      // layout (clientWidth is 0 before the element is laid out).
      const mounted = prop(0)
      requestAnimationFrame(() => mounted.set(1))
      const scale = delaySignal(
        computedOf(
          rect,
          size,
          mounted
        )((_r, s, _m) => {
          const holder = ctx.element as HTMLElement
          const w = holder.clientWidth
          if (!w || w <= 0) return 1
          return Math.min(1, w / s.w)
        }),
        50
      )
      return html.div(
        attr.style(
          computedOf(
            scale,
            size
          )((sc, s) => `width:100%; height:${Math.round(s.h * sc)}px;`)
        ),
        html.div(
          attr.style(
            computedOf(
              scale,
              size
            )(
              (sc, s) =>
                `width:${s.w}px; height:${s.h}px; transform:scale(${sc}); transform-origin:top left; position:relative;`
            )
          ),
          ...content
        )
      )
    })
  )
}
